import { useState, useEffect } from "react";
import { makeTr } from "@/i18n/tr";
// Force rebuild v3
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useStore } from "@/store/useStore";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Slider } from "@/components/ui/slider";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { 
  Bot,
  Loader2,
  Clock,
  ArrowLeft,
  Sparkles
} from "lucide-react";
import { API_URL } from "@/config/api";

// 10 уровней ботов как в Lichess/Chess.com
const botLevels = [
  { level: 1, name: "Новичок", elo: 400, color: "bg-green-400" },
  { level: 2, name: "Начинающий", elo: 600, color: "bg-green-500" },
  { level: 3, name: "Любитель", elo: 800, color: "bg-lime-500" },
  { level: 4, name: "Игрок клуба", elo: 1000, color: "bg-yellow-500" },
  { level: 5, name: "Опытный", elo: 1300, color: "bg-amber-500" },
  { level: 6, name: "Сильный", elo: 1600, color: "bg-orange-500" },
  { level: 7, name: "Кандидат", elo: 1900, color: "bg-orange-600" },
  { level: 8, name: "Мастер", elo: 2200, color: "bg-red-500" },
  { level: 9, name: "Гроссмейстер", elo: 2700, color: "bg-red-600" },
  { level: 10, name: "Сверхгроссмейстер", elo: 3300, color: "bg-purple-600" },
];

export default function PlayBotPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { token } = useStore();
  
  const [level, setLevel] = useState(5);
  const [playerColor, setPlayerColor] = useState("random");
  const [timeControl, setTimeControl] = useState("600");
  const [increment, setIncrement] = useState("0");
  const [loading, setLoading] = useState(false);

  const isRussian = i18n.language === 'ru';
  const tr = makeTr(i18n.language);

  const currentBot = botLevels[level - 1];

  const handleStartGame = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/bot/games`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          difficulty: `level_${level}`,
          player_color: playerColor === "random" ? (Math.random() < 0.5 ? "white" : "black") : playerColor,
          time_control: parseInt(timeControl),
          increment: parseInt(increment)
        })
      });

      const data = await res.json();
      
      if (res.ok) {
        toast.success(tr("Игра создана!", "Game created!"));
        navigate(`/bot-game/${data.id}`);
      } else {
        toast.error(data.detail || t("common.error"));
      }
    } catch (err) {
      toast.error(t("common.error"));
    } finally {
      setLoading(false);
    }
  };

  const timeOptions = [
    { value: "60", label: `1 ${tr("мин", "min")} (Bullet)` },
    { value: "180", label: `3 ${tr("мин", "min")} (Blitz)` },
    { value: "300", label: `5 ${tr("мин", "min")} (Blitz)` },
    { value: "600", label: `10 ${tr("мин", "min")} (Rapid)` },
    { value: "900", label: `15 ${tr("мин", "min")} (Rapid)` },
    { value: "1800", label: `30 ${tr("мин", "min")} (Classical)` },
  ];

  const incrementOptions = [
    { value: "0", label: tr("Без добавки", "No increment") },
    { value: "1", label: "+1 сек" },
    { value: "2", label: "+2 сек" },
    { value: "3", label: "+3 сек" },
    { value: "5", label: "+5 сек" },
    { value: "10", label: "+10 сек" },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-4 sm:space-y-6 px-2 sm:px-4" data-testid="play-bot-page">
      {/* Back button */}
      <Button 
        variant="ghost" 
        onClick={() => navigate("/lobby")}
        className="mb-1 sm:mb-2 h-8 sm:h-10 text-sm"
        size="sm"
      >
        <ArrowLeft className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" />
        {t("common.back")}
      </Button>

      <div className="text-center mb-4 sm:mb-8">
        <h1 className="text-2xl sm:text-3xl font-black tracking-tight mb-1 sm:mb-2">
          {tr("Игра с ботом", "Play vs Bot")}
        </h1>
        <p className="text-sm sm:text-base text-muted-foreground">
          {tr("10 уровней сложности", "10 difficulty levels")}
        </p>
      </div>

      {/* Bot Level Selection */}
      <Card className="glass border-border/50">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Bot className="h-4 w-4 sm:h-5 sm:w-5 text-primary" />
            {tr("Уровень бота", "Bot Level")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 sm:space-y-6">
          {/* Current Bot Display */}
          <div className="text-center p-4 sm:p-6 rounded-xl bg-gradient-to-br from-zinc-800/50 to-zinc-900/50 border border-border/50">
            <div className={`inline-flex items-center justify-center h-14 w-14 sm:h-20 sm:w-20 rounded-full ${currentBot.color} mb-2 sm:mb-4`}>
              <span className="text-2xl sm:text-3xl font-black text-white">{level}</span>
            </div>
            <h3 className="text-xl sm:text-2xl font-bold mb-1">{t(`game.levelNames.${currentBot.level}`, currentBot.name)}</h3>
            <p className="text-sm sm:text-base text-muted-foreground flex items-center justify-center gap-1">
              <Sparkles className="h-3 w-3 sm:h-4 sm:w-4" />
              {currentBot.level === 10 ? tr("3300+ ELO · сильнее чемпиона мира", "3300+ ELO · stronger than the world champion") : `~${currentBot.elo} ELO`}
            </p>
          </div>

          {/* Level Slider */}
          <div className="space-y-3 sm:space-y-4">
            <div className="flex justify-between text-xs sm:text-sm text-muted-foreground">
              <span>{tr("Легче", "Easier")}</span>
              <span>{tr("Сложнее", "Harder")}</span>
            </div>
            <Slider
              value={[level]}
              onValueChange={(v) => setLevel(v[0])}
              min={1}
              max={10}
              step={1}
              className="cursor-pointer"
              data-testid="level-slider"
            />
            {/* Level indicators */}
            <div className="flex justify-between px-0.5 sm:px-1">
              {botLevels.map((bot) => (
                <button
                  key={bot.level}
                  onClick={() => setLevel(bot.level)}
                  className={`w-6 h-6 sm:w-7 sm:h-7 rounded-full text-xs font-bold transition-all ${
                    level === bot.level 
                      ? `${bot.color} text-white scale-110 sm:scale-125` 
                      : "bg-zinc-700 text-zinc-400 hover:bg-zinc-600"
                  }`}
                >
                  {bot.level}
                </button>
              ))}
            </div>
          </div>

          {/* Level descriptions - hide on mobile */}
          <div className="hidden sm:grid grid-cols-5 gap-1 text-center text-xs">
            <div className="text-green-400">1-2<br/>{tr("Новичок", "Beginner")}</div>
            <div className="text-yellow-500">3-4<br/>{tr("Любитель", "Casual")}</div>
            <div className="text-orange-500">5-6<br/>{tr("Средний", "Medium")}</div>
            <div className="text-red-500">7-8<br/>{tr("Сильный", "Strong")}</div>
            <div className="text-purple-500">9-10<br/>{tr("Эксперт", "Expert")}</div>
          </div>
        </CardContent>
      </Card>

      {/* Color Selection */}
      <Card className="glass border-border/50">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="text-base sm:text-lg">{tr("Выберите цвет", "Choose Color")}</CardTitle>
        </CardHeader>
        <CardContent className="flex gap-2 sm:gap-4">
          <button
            onClick={() => setPlayerColor("white")}
            data-testid="color-white"
            className={`
              flex-1 p-3 sm:p-6 rounded-lg border-2 transition-all
              ${playerColor === "white" 
                ? "bg-white/10 border-white" 
                : "border-border/50 hover:border-border"
              }
            `}
          >
            <div className="h-8 w-8 sm:h-12 sm:w-12 mx-auto rounded-full bg-white mb-2 sm:mb-3 shadow-lg" />
            <p className="font-semibold text-sm sm:text-base">{tr("Белые", "White")}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">{tr("Первый ход", "First move")}</p>
          </button>
          
          <button
            onClick={() => setPlayerColor("random")}
            data-testid="color-random"
            className={`
              flex-1 p-3 sm:p-6 rounded-lg border-2 transition-all
              ${playerColor === "random" 
                ? "bg-primary/10 border-primary" 
                : "border-border/50 hover:border-border"
              }
            `}
          >
            <div
              className="h-8 w-8 sm:h-12 sm:w-12 mx-auto rounded-full mb-2 sm:mb-3 shadow-lg border-2 border-zinc-500"
              style={{ background: "linear-gradient(90deg, #ffffff 50%, #18181b 50%)" }}
            />
            <p className="font-semibold text-sm sm:text-base">{tr("Случайно", "Random")}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">{tr("Цвет выберется автоматически", "Color picked automatically")}</p>
          </button>

          <button
            onClick={() => setPlayerColor("black")}
            data-testid="color-black"
            className={`
              flex-1 p-3 sm:p-6 rounded-lg border-2 transition-all
              ${playerColor === "black" 
                ? "bg-zinc-800/50 border-zinc-500" 
                : "border-border/50 hover:border-border"
              }
            `}
          >
            <div className="h-8 w-8 sm:h-12 sm:w-12 mx-auto rounded-full bg-zinc-900 border-2 border-zinc-600 mb-2 sm:mb-3" />
            <p className="font-semibold text-sm sm:text-base">{tr("Чёрные", "Black")}</p>
            <p className="text-[10px] sm:text-xs text-muted-foreground">{tr("Второй ход", "Second move")}</p>
          </button>
        </CardContent>
      </Card>

      {/* Time Control */}
      <Card className="glass border-border/50">
        <CardHeader className="pb-2 sm:pb-4">
          <CardTitle className="flex items-center gap-2 text-base sm:text-lg">
            <Clock className="h-4 w-4 sm:h-5 sm:w-5" />
            {tr("Контроль времени", "Time Control")}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 sm:space-y-4">
          <div className="grid grid-cols-2 gap-2 sm:gap-4">
            <div>
              <label className="text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2 block">
                {tr("Время на игру", "Game time")}
              </label>
              <Select value={timeControl} onValueChange={setTimeControl}>
                <SelectTrigger data-testid="time-select" className="h-9 sm:h-10 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timeOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-xs sm:text-sm text-muted-foreground mb-1 sm:mb-2 block">
                {tr("Добавка за ход", "Increment")}
              </label>
              <Select value={increment} onValueChange={setIncrement}>
                <SelectTrigger data-testid="increment-select" className="h-9 sm:h-10 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {incrementOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Start Game Button */}
      <Button 
        className="w-full h-12 sm:h-14 text-base sm:text-lg font-bold"
        onClick={handleStartGame}
        disabled={loading}
        data-testid="start-game-btn"
      >
        {loading ? (
          <Loader2 className="h-4 w-4 sm:h-5 sm:w-5 animate-spin mr-2" />
        ) : (
          <Bot className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
        )}
        {isRussian 
          ? `Играть vs ${t(`game.levelNames.${currentBot.level}`, currentBot.name)}` 
          : `${tr("Играть vs", "Play vs")} ${t(`game.levelNames.${currentBot.level}`, currentBot.name)}`
        }
      </Button>
    </div>
  );
}
